/**
 * useFabClearance / useFabObstruction — RW-1486.
 *
 * The floating buttons (QuickActionsFab, BugReporter) are fixed to the
 * bottom-right corner, which is exactly where pages with a bottom-pinned
 * composer put their Send button — on /ask-reve the FAB sat on top of it.
 *
 * Pages register their bottom-pinned bar with useFabObstruction(ref); the
 * floating buttons read useFabClearance() and lift themselves above the
 * tallest registered bar. Evented (ResizeObserver + window resize), so
 * banners growing the composer or a resize re-clear automatically.
 *
 * RW-2953: a page's own create button can also say it REPLACES the global
 * quick-actions "+" (`useFabObstruction({ replacesQuickActions: true })`).
 * On the phone feed that left two stacked coral "+" buttons; while such a
 * button is mounted, QuickActionsFab reads useQuickActionsReplaced() and
 * steps aside, so the page has one create entry.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const obstructions = new Map(); // id → px the bar reaches up from the viewport bottom
const listeners = new Set();
let nextId = 1;

const replacers = new Set(); // ids of mounted page create buttons that replace quick actions
const replaceListeners = new Set();
const emitReplace = () => { const n = replacers.size; replaceListeners.forEach((fn) => fn(n)); };

/** True while a page create button that replaces the quick-actions "+" is mounted. */
export function useQuickActionsReplaced() {
  const [n, setN] = useState(() => replacers.size);
  useEffect(() => {
    replaceListeners.add(setN);
    setN(replacers.size);
    return () => { replaceListeners.delete(setN); };
  }, []);
  return n > 0;
}

const clearance = () => (obstructions.size ? Math.max(...obstructions.values()) : 0);
const emit = () => { const c = clearance(); listeners.forEach((fn) => fn(c)); };

/** Px from the viewport bottom the floating buttons must clear (0 = none). */
export function useFabClearance() {
  const [lift, setLift] = useState(clearance);
  useEffect(() => {
    listeners.add(setLift);
    setLift(clearance());
    return () => listeners.delete(setLift);
  }, []);
  return lift;
}

/**
 * Callback ref for a bottom-pinned bar. While the node is mounted, its
 * viewport-bottom reach (innerHeight − rect.top) is registered live.
 */
export function useFabObstruction({ replacesQuickActions = false } = {}) {
  const cleanupRef = useRef(null);
  const setRef = useCallback((node) => {
    if (cleanupRef.current) { cleanupRef.current(); cleanupRef.current = null; }
    if (!node) return;
    const id = nextId++;
    if (replacesQuickActions) { replacers.add(id); emitReplace(); }
    const measure = () => {
      const reach = Math.max(0, Math.round(window.innerHeight - node.getBoundingClientRect().top));
      if (obstructions.get(id) !== reach) { obstructions.set(id, reach); emit(); }
    };
    measure();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(node);
    }
    window.addEventListener('resize', measure);
    cleanupRef.current = () => {
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
      if (obstructions.delete(id)) emit();
      if (replacers.delete(id)) emitReplace();
    };
  }, [replacesQuickActions]);
  useEffect(() => () => { if (cleanupRef.current) { cleanupRef.current(); cleanupRef.current = null; } }, []);
  return setRef;
}
